import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getForumPosts, getForumPost, createForumPost, createForumReply, voteOnPoll, deleteForumPost, togglePinPost } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { MessageSquare, Plus, Send, Pin, Trash2, User, Clock, BarChart3, CheckCircle2, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const Forum = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general',
    is_poll: false,
    poll_options: ['', '']
  });

  const categories = [
    { value: 'all', label: 'All Posts' },
    { value: 'general', label: 'General' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'suggestions', label: 'Suggestions' },
    { value: 'lost_found', label: 'Lost & Found' },
    { value: 'help', label: 'Help Needed' }
  ];

  const fetchPosts = async () => {
    try {
      const data = await getForumPosts(activeCategory);
      setPosts(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load forum posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchPostDetails = async (postId) => {
    try {
      const data = await getForumPost(postId);
      setSelectedPost(data);
    } catch (error) {
      toast.error('Failed to load post details');
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [activeCategory]);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setSubmitting(true);
      const postData = {
        title: formData.title,
        content: formData.content,
        category: formData.category,
        is_poll: formData.is_poll,
        poll_options: formData.is_poll ? formData.poll_options.filter(o => o.trim()) : null
      };
      await createForumPost(postData);
      toast.success('Post created successfully');
      setDialogOpen(false);
      setFormData({ title: '', content: '', category: 'general', is_poll: false, poll_options: ['', ''] });
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    try {
      setSubmitting(true);
      await createForumReply(selectedPost.id, replyContent);
      toast.success('Reply added');
      setReplyContent('');
      fetchPostDetails(selectedPost.id);
    } catch (error) {
      toast.error('Failed to add reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (option) => {
    try {
      await voteOnPoll(selectedPost.id, option);
      toast.success('Vote recorded');
      fetchPostDetails(selectedPost.id);
    } catch (error) {
      toast.error('Failed to record vote');
    }
  };

  const handleDelete = async (postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      await deleteForumPost(postId);
      toast.success('Post deleted');
      setSelectedPost(null);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const handlePin = async (postId) => {
    try {
      await togglePinPost(postId);
      toast.success('Post pin status updated');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to update pin status');
    }
  };

  const getCategoryColor = (cat) => {
    const colors = {
      general: 'bg-blue-100 text-blue-700',
      maintenance: 'bg-orange-100 text-orange-700',
      suggestions: 'bg-green-100 text-green-700',
      lost_found: 'bg-amber-100 text-amber-700',
      help: 'bg-rose-100 text-rose-700'
    };
    return colors[cat] || 'bg-slate-100 text-slate-700';
  };

  const addPollOption = () => {
    setFormData({ ...formData, poll_options: [...formData.poll_options, ''] });
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>;
  }

  // Post Detail View
  if (selectedPost) {
    const totalVotes = selectedPost.is_poll 
      ? Object.values(selectedPost.poll_votes || {}).reduce((sum, voters) => sum + voters.length, 0)
      : 0;

    return (
      <div className="space-y-6" data-testid="forum-post-detail">
        <Button variant="ghost" onClick={() => setSelectedPost(null)} className="mb-4">
          <ChevronLeft className="h-4 w-4 mr-2" /> Back to Forum
        </Button>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {selectedPost.is_pinned && <Pin className="h-4 w-4 text-amber-500" />}
                  <Badge className={getCategoryColor(selectedPost.category)}>{selectedPost.category}</Badge>
                  {selectedPost.is_poll && <Badge className="bg-purple-100 text-purple-700"><BarChart3 className="h-3 w-3 mr-1" />Poll</Badge>}
                </div>
                <CardTitle className="text-xl">{selectedPost.title}</CardTitle>
                <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                  <User className="h-4 w-4" />
                  <span>{selectedPost.author_name}</span>
                  {selectedPost.author_unit && <span>• Unit {selectedPost.author_unit}</span>}
                  <Clock className="h-4 w-4 ml-2" />
                  <span>{new Date(selectedPost.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="flex gap-2">
                {user?.role === 'admin' && (
                  <Button variant="outline" size="sm" onClick={() => handlePin(selectedPost.id)}>
                    <Pin className="h-4 w-4" />
                  </Button>
                )}
                {(selectedPost.author_id === user?.id || user?.role === 'admin') && (
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedPost.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700 whitespace-pre-wrap mb-6">{selectedPost.content}</p>

            {/* Poll Section */}
            {selectedPost.is_poll && selectedPost.poll_votes && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" /> Poll Results ({totalVotes} votes)
                </h4>
                <div className="space-y-3">
                  {Object.entries(selectedPost.poll_votes).map(([option, voters]) => {
                    const percentage = totalVotes > 0 ? (voters.length / totalVotes) * 100 : 0;
                    const hasVoted = voters.includes(user?.id);
                    return (
                      <div key={option} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => handleVote(option)}
                            className={`flex items-center gap-2 text-left ${hasVoted ? 'font-semibold text-primary' : ''}`}
                          >
                            {hasVoted && <CheckCircle2 className="h-4 w-4 text-primary" />}
                            {option}
                          </button>
                          <span className="text-sm text-slate-500">{voters.length} ({percentage.toFixed(0)}%)</span>
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${hasVoted ? 'bg-primary' : 'bg-slate-400'} transition-all`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Replies Section */}
            <div className="border-t pt-6">
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" /> Replies ({selectedPost.replies?.length || 0})
              </h4>
              
              <div className="space-y-4 mb-6">
                {selectedPost.replies?.map((reply) => (
                  <div key={reply.id} className={`p-4 rounded-lg ${reply.is_admin_reply ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-2 mb-2 text-sm">
                      <User className="h-4 w-4" />
                      <span className="font-medium">{reply.author_name}</span>
                      {reply.is_admin_reply && <Badge className="bg-blue-100 text-blue-700 text-xs">Admin</Badge>}
                      {reply.author_unit && <span className="text-slate-500">• Unit {reply.author_unit}</span>}
                      <span className="text-slate-400">• {new Date(reply.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-slate-700">{reply.content}</p>
                  </div>
                ))}
                {(!selectedPost.replies || selectedPost.replies.length === 0) && (
                  <p className="text-slate-500 text-center py-4">No replies yet. Be the first to reply!</p>
                )}
              </div>

              {/* Reply Form */}
              <div className="flex gap-2">
                <Textarea
                  placeholder="Write your reply..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  className="flex-1"
                  data-testid="reply-input"
                />
                <Button onClick={handleReply} disabled={submitting} data-testid="submit-reply-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forum List View
  return (
    <div className="space-y-6" data-testid="forum-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Community Forum</h1>
          <p className="text-sm md:text-base text-slate-600">Connect with your neighbors and discuss community matters</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-secondary hover:bg-secondary/90" data-testid="new-post-btn">
              <Plus className="h-4 w-4 mr-2" /> New Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Post</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreatePost} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="Enter post title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="post-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger data-testid="post-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="suggestions">Suggestions</SelectItem>
                    <SelectItem value="lost_found">Lost & Found</SelectItem>
                    <SelectItem value="help">Help Needed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Content</Label>
                <Textarea
                  id="content"
                  placeholder="Write your post content..."
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={4}
                  data-testid="post-content-input"
                />
              </div>

              {user?.role === 'admin' && (
                <div className="space-y-4 border-t pt-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_poll"
                      checked={formData.is_poll}
                      onChange={(e) => setFormData({ ...formData, is_poll: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="is_poll">Create as Poll (Admin only)</Label>
                  </div>

                  {formData.is_poll && (
                    <div className="space-y-2">
                      <Label>Poll Options</Label>
                      {formData.poll_options.map((opt, idx) => (
                        <Input
                          key={idx}
                          placeholder={`Option ${idx + 1}`}
                          value={opt}
                          onChange={(e) => {
                            const newOptions = [...formData.poll_options];
                            newOptions[idx] = e.target.value;
                            setFormData({ ...formData, poll_options: newOptions });
                          }}
                        />
                      ))}
                      <Button type="button" variant="outline" size="sm" onClick={addPollOption}>
                        Add Option
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-post-btn">
                {submitting ? 'Creating...' : 'Create Post'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="flex-wrap h-auto">
          {categories.map((cat) => (
            <TabsTrigger key={cat.value} value={cat.value} className="text-xs sm:text-sm">
              {cat.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Posts List */}
      <div className="space-y-4">
        {posts.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <MessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No posts yet. Start a conversation!</p>
            </CardContent>
          </Card>
        ) : (
          posts.map((post) => (
            <Card 
              key={post.id} 
              className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => fetchPostDetails(post.id)}
              data-testid="forum-post-card"
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="h-5 w-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {post.is_pinned && <Pin className="h-4 w-4 text-amber-500" />}
                      <h3 className="font-semibold text-slate-900 truncate">{post.title}</h3>
                    </div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className={getCategoryColor(post.category)}>{post.category}</Badge>
                      {post.is_poll && <Badge className="bg-purple-100 text-purple-700"><BarChart3 className="h-3 w-3 mr-1" />Poll</Badge>}
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-2">{post.content}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" /> {post.author_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" /> {post.replies_count} replies
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {new Date(post.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Forum;
